import { useRouter } from '@/domains/shared/router/Router';
import { Button } from '@/domains/shared/components/Button';
import { Card, CardTitle, CardDescription } from '@/domains/shared/components/Card';
import { DIAGNOSTIC_CATEGORIES } from '@/domains/assessment/constants';
import { useDiagnostic } from '@/contexts/DiagnosticContext';

export function LandingPage() {
  const { navigate } = useRouter();
  const { resetDiagnostic } = useDiagnostic();

  const handleStart = () => {
    resetDiagnostic();
    navigate('/instrucoes');
  };

  return (
    <div className="flex min-h-screen flex-col bg-navy text-brand-offWhite">
      {/* Topo institucional */}
      <header className="border-b border-white/10 bg-navy-dark/90 backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3.5 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-orange font-black text-white shadow-md">
              IC
            </div>
            <div>
              <span className="block text-[10px] font-extrabold uppercase tracking-wider text-brand-orange">
                Israel Costa & DecolaCont
              </span>
              <span className="block text-xs font-bold text-white sm:text-sm">
                Diagnóstico de Presença Estratégica
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-brand-orange bg-brand-orange/10 px-2.5 py-1 rounded-full border border-brand-orange/20">
              Versão Gratuita
            </span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col items-center justify-center px-4 py-12 text-center sm:px-6 sm:py-16">
        <div className="inline-flex items-center gap-2 rounded-full border border-brand-orange/40 bg-brand-orange/10 px-4 py-1.5 text-xs font-semibold text-brand-orange shadow-sm">
          <span className="h-2 w-2 rounded-full bg-brand-orange animate-pulse" />
          Para Donos e Sócios de Escritórios Contábeis
        </div>

        <h1 className="mt-6 max-w-3xl text-3xl font-extrabold tracking-tight text-white sm:text-5xl sm:leading-tight">
          Em menos de 5 minutos, descubra onde seu escritório está{' '}
          <span className="text-brand-orange">perdendo oportunidades</span>
        </h1>

        <p className="mt-5 max-w-2xl text-base text-brand-offWhite/80 sm:text-lg">
          Olhe para a realidade da sua operação contábil. Avalie 6 dimensões estratégicas e receba uma devolutiva personalizada com uma prioridade recomendada para o seu momento.
        </p>

        {/* Chamada para ação principal */}
        <div className="mt-8 flex flex-col items-center gap-3 w-full sm:w-auto">
          <Button
            variant="primary"
            size="lg"
            onClick={handleStart}
            className="w-full sm:w-auto text-base sm:text-lg px-8 py-4 shadow-xl shadow-brand-orange/25"
          >
            Começar Diagnóstico Gratuito →
          </Button>

          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-brand-offWhite/60 mt-2">
            <span className="flex items-center gap-1.5">
              <svg className="h-4 w-4 text-feedback-success" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              18 perguntas objetivas
            </span>
            <span className="flex items-center gap-1.5">
              <svg className="h-4 w-4 text-feedback-success" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Veja onde agir primeiro
            </span>
            <span className="flex items-center gap-1.5">
              <svg className="h-4 w-4 text-feedback-success" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              resultado imediato
            </span>
          </div>
        </div>

        {/* As 6 Dimensões Mapeadas */}
        <section className="mt-16 w-full text-left" aria-labelledby="dimensoes-title">
          <div className="text-center mb-8">
            <h2 id="dimensoes-title" className="text-xl font-bold text-white sm:text-2xl">
              As 6 Dimensões Avaliadas no Seu Escritório
            </h2>
            <p className="mt-2 text-xs text-brand-offWhite/70 sm:text-sm">
              Vamos arrumar a casa: cada dimensão reúne três perguntas sobre a realidade do seu escritório.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {DIAGNOSTIC_CATEGORIES.map((category, index) => (
              <Card key={category.key} variant="interactive" className="p-5">
                <span className="inline-block rounded bg-brand-orange/20 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-brand-orange">
                  Dimensão 0{index + 1}
                </span>
                <CardTitle className="mt-2 text-base text-white">
                  {category.name}
                </CardTitle>
                <CardDescription className="mt-1 text-xs text-brand-offWhite/70">
                  3 perguntas práticas para diagnosticar maturidade, gargalos e capacidade de execução.
                </CardDescription>
              </Card>
            ))}
          </div>
        </section>
      </main>

      {/* Rodapé */}
      <footer className="border-t border-white/10 bg-navy-dark py-8 text-center text-xs text-brand-offWhite/60">
        <div className="mx-auto max-w-5xl px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© 2026 Israel Costa & DecolaCont. Todos os direitos reservados.</p>
          <p className="text-[11px] text-brand-offWhite/50">Ambiente seguro com conformidade LGPD e WCAG AA.</p>
        </div>
      </footer>
    </div>
  );
}
